import React, {
  useState, useEffect, useRef
} from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import Imgix from 'react-imgix'
import { Link } from 'react-router-dom'
import { merge, interval, EMPTY } from 'rxjs'
import { switchMap, mapTo, startWith } from 'rxjs/operators'

import {
  PLAY_BUTTON_NEXT_EPISODE_TEXT,
  PLAY_BUTTON_NEXT_SEASON_TEXT,
  CLOSE_BUTTON_WATCH_CREDITS_TEXT,
  INTERVALS
} from '../../../constants'
import Button from '../../../../button'
import { StateConsumer } from '../../../state'
import styles from './next-episode-countdown.css'

/**
 * Displays a banner overlay during video playback that counts down
 * to the next episode. Shows episode thumbnail, title, and a live countdown timer.
 *
 * The countdown pauses/resumes in sync with the video player's play/pause state via RxJS streams.
 * When the countdown reaches zero, it either:
 *  - Shows a "continue watching" prompt (if applicable), or
 *  - Automatically redirects to the next episode.
 *
 * The banner is hidden when an ad-on-pause overlay or the episode selector is open.
 */
const NextEpisodeCountdownUi = ({
  id,
  nextEpisodeSeason,
  nextEpisodeNumber,
  currentSeason,
  title,
  imageUrl,
  getStreams,
  countdownTime,
  redirectToEpisodeById,
  getShouldDisplayContinueWatching,
  showContinueWatching,
  setShouldUseCuePoint,
  dismissBanner,
  isPlaying
}) => {
  const [timeRemaining, setTimeRemaining] = useState(countdownTime)
  const subscriptionRef = useRef(null)

  // Unsubscribes the countdown timer to stop it from ticking
  const clearCountDown = () => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
    }
  }

  // Called when user clicks "Watch Credits" — hides the banner and stops the countdown
  const dismissCountdown = () => {
    dismissBanner()
    clearCountDown()
  }

  // Initialises the countdown timer using RxJS.
  // Listens to play/pause streams to pause the countdown when the video is paused.
  // On each tick, decrements timeRemaining. At zero, triggers navigation or continue-watching.
  useEffect(() => {
    const countdown$ = interval(INTERVALS.NEXT_EPISODE_COUNTDOWN)
    const pauseEvent$ = getStreams().pause$.pipe(mapTo(false))
    const playEvent$ = getStreams().play$.pipe(mapTo(true))
    const counting$ = merge(pauseEvent$, playEvent$).pipe(
      startWith(isPlaying),
      // playing is the mapped value from pauseEvent$ or playEvent$
      // and will stop the countdown ticking while player is paused
      switchMap(playing => (playing ? countdown$ : EMPTY))
    )

    subscriptionRef.current = counting$.subscribe(() => {
      setTimeRemaining(prev => {
        const nextValue = prev - 1
        if (nextValue < 0) {
          if (getShouldDisplayContinueWatching()) {
            showContinueWatching()
          } else if (id) {
            redirectToEpisodeById(id)
            if (subscriptionRef.current) {
              subscriptionRef.current.unsubscribe()
            }
          }
          return prev
        }
        return nextValue
      })
    })

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [getStreams, isPlaying, id, redirectToEpisodeById, getShouldDisplayContinueWatching, showContinueWatching])

  const pluralise = timeRemaining !== 1 ? 's' : ''

  const episodeOrSeason =
    nextEpisodeSeason > currentSeason ? 'season' : 'episode'

  const ctaPrefix = nextEpisodeSeason > currentSeason
    ? PLAY_BUTTON_NEXT_SEASON_TEXT
    : PLAY_BUTTON_NEXT_EPISODE_TEXT

  return (
    <StateConsumer>
      {({
        isAdOnPauseShown,
        isEpisodeSelectorOpen
      }) => {
        return (
          <div
            className={classnames(styles.wrapper, {
              [styles.show]: !isAdOnPauseShown && !isEpisodeSelectorOpen
            })}
          >
            <div className={styles.thumbnail}>
              <Imgix src={imageUrl} width={290} height={164} className={styles.roundedBorder} />
            </div>
            <div className={styles.info}>
              <h2 className={classnames(styles.title, styles.header)}>
                {`Next ${episodeOrSeason} in `}
                <span>
                  {`${timeRemaining} second${pluralise}`}
                </span>
              </h2>
              <h3 className={classnames(styles.title, styles.episode)}>
                {`S${nextEpisodeSeason} E${nextEpisodeNumber} '${title}'`}
              </h3>
              <div className={styles.buttonWrapper}>
                <Link
                  to={id}
                  onClick={() => {
                    setShouldUseCuePoint()
                  }}
                >
                  <Button
                    className={styles.button}
                    text={ctaPrefix}
                  />
                </Link>
                <Button
                  className={classnames(styles.button, styles.creditsButton)}
                  onClick={dismissCountdown}
                  text={CLOSE_BUTTON_WATCH_CREDITS_TEXT}
                  darkBGSecondary
                />
              </div>
            </div>
          </div>
        )
      }}
    </StateConsumer>
  )
}

NextEpisodeCountdownUi.propTypes = {
  id: PropTypes.string.isRequired,
  nextEpisodeSeason: PropTypes.number.isRequired,
  nextEpisodeNumber: PropTypes.number.isRequired,
  currentSeason: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  getStreams: PropTypes.func.isRequired,
  countdownTime: PropTypes.number.isRequired,
  redirectToEpisodeById: PropTypes.func.isRequired,
  getShouldDisplayContinueWatching: PropTypes.func.isRequired,
  showContinueWatching: PropTypes.func.isRequired,
  setShouldUseCuePoint: PropTypes.func.isRequired,
  dismissBanner: PropTypes.func.isRequired,
  isPlaying: PropTypes.bool.isRequired
}

export default NextEpisodeCountdownUi
